import { useEffect, useState, createContext, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuthStore } from '../../stores/root_store';
import { teamAdminApi, type FHTeamDetail } from '../../api/team_admin_api';

// Context to share the fetched team detail with child tab routes.
interface ManagerTeamContextType {
  detail: FHTeamDetail | null;
  loading: boolean;
  error: string | null;
}

const ManagerTeamContext = createContext<ManagerTeamContextType | null>(null);

export const useManagerTeam = () => {
  const context = useContext(ManagerTeamContext);
  if (!context) {
    throw new Error('useManagerTeam must be used within ManagerTeamPage');
  }
  return context;
};

export const ManagerTeamPage = observer(() => {
  const { teamKey } = useParams<{ teamKey: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();

  const [detail, setDetail] = useState<FHTeamDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUserHasManager = authStore.roles.includes('manager');

  // Determine active tab from URL.
  const getTabValue = () => {
    if (location.pathname.endsWith('/invites')) return 1;
    if (location.pathname.endsWith('/payments')) return 2;
    if (location.pathname.endsWith('/billing')) return 3;
    return 0;
  };

  useEffect(() => {
    if (!teamKey) return;

    const fetchTeam = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await teamAdminApi.detail(teamKey);
        setDetail(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load team');
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamKey]);

  // Redirect if not a manager
  if (authStore.initialized && !currentUserHasManager) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !detail) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manager/teams')}
          sx={{ mt: 2 }}
        >
          Back to Teams
        </Button>
      </Container>
    );
  }

  return (
    <ManagerTeamContext.Provider value={{ detail, loading, error }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/manager/teams')}
          sx={{ mb: 2 }}
        >
          Back to Teams
        </Button>

        <Typography variant="h4" sx={{ mb: 2 }}>
          {detail?.team.team_name || 'Team'}
        </Typography>

        <Tabs value={getTabValue()} sx={{ mb: 3 }}>
          <Tab label="Overview" component={Link} to={`/manager/teams/${teamKey}`} />
          <Tab label="Invites" component={Link} to={`/manager/teams/${teamKey}/invites`} />
          <Tab label="Payments" component={Link} to={`/manager/teams/${teamKey}/payments`} />
          <Tab label="Billing" component={Link} to={`/manager/teams/${teamKey}/billing`} />
        </Tabs>

        <Outlet />
      </Container>
    </ManagerTeamContext.Provider>
  );
});
